/**
 * BaseLinkerConnectPanel – BaseLinker integration panel.
 * API token input (no OAuth). Validates token, fetches orders.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ShoppingBag, TrendingUp, Package, ChevronDown, ChevronUp } from "lucide-react";

/** Odpowiedz /api/integrations/baselinker/insights. */
type BaselinkerInsights = {
  context?: string;
  summary: {
    totalOrders?: number;
    totalRevenue?: number;
    totalProducts?: number;
    currency?: string;
    topProducts: Array<{ name?: string; quantity?: number | string; revenue?: number | string }>;
  };
};

interface BaseLinkerStatus {
  connected: boolean;
  connectedAt?: string;
}
interface BaseLinkerConnectPanelProps {
  onInsightsLoaded?: (context: string) => void;
  forUserId?: string;
}

const BaseLinkerLogo = () => (
  <div className="w-6 h-6 rounded bg-[#0066CC] flex items-center justify-center">
    <span className="text-white font-bold text-xs">BL</span>
  </div>
);

export default function BaseLinkerConnectPanel({
  onInsightsLoaded,
  forUserId,
}: BaseLinkerConnectPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [apiToken, setApiToken] = useState("");
  const [insightsData, setInsightsData] = useState<BaselinkerInsights | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const fuParam = forUserId ? `?forUserId=${forUserId}` : "";

  const { data: status, isLoading } = useQuery<BaseLinkerStatus>({
    queryKey: ["baselinker-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/baselinker/status${fuParam}`),
    staleTime: 30_000,
  });

  const connectMutation = useMutation({
    mutationFn: (token: string) =>
      apiRequest("POST", "/api/integrations/baselinker/connect", { apiToken: token }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["baselinker-integration-status"] });
      setApiToken("");
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/integrations/baselinker/disconnect"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["baselinker-integration-status"] });
      setInsightsData(null);
    },
  });

  const insightsMutation = useMutation({
    mutationFn: () =>
      apiRequest<BaselinkerInsights>("POST", "/api/integrations/baselinker/insights"),
    onSuccess: (data: BaselinkerInsights) => {
      setInsightsData(data);
      onInsightsLoaded?.(data.context ?? "");
    },
  });

  if (isLoading) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-blue-400" />
        <span className="text-sm text-white/60">{t("connectPanels.baselinker.checking")}</span>
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BaseLinkerLogo />
            <span className="text-sm font-medium text-white">BaseLinker</span>
            <Badge variant="outline" className="text-xs border-white/[0.1] text-white/60">
              {t("connectPanels.common.notConnected")}
            </Badge>
          </div>
        </div>
        <p className="text-xs text-white/40">{t("connectPanels.baselinker.hint")}</p>
        <div className="flex items-center gap-2">
          <input
            type="password"
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            placeholder={t("connectPanels.baselinker.tokenPlaceholder")}
            className="flex-1 h-8 px-3 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-[#0066CC]/50"
          />
          <Button
            size="sm"
            onClick={() => connectMutation.mutate(apiToken)}
            disabled={connectMutation.isPending || apiToken.trim().length < 10}
            className="bg-[#0066CC] hover:bg-[#0052a3] text-white text-xs"
          >
            {connectMutation.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-1" />{" "}
                {t("connectPanels.baselinker.checkingButton")}
              </>
            ) : (
              t("connectPanels.common.connect")
            )}
          </Button>
        </div>
        {connectMutation.isError && (
          <p className="text-xs text-red-400">
            {(connectMutation.error as Error | null)?.message ||
              t("connectPanels.baselinker.tokenInvalid")}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="p-3 bg-white/[0.04] border border-[#0066CC]/30 rounded-lg space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BaseLinkerLogo />
          <span className="text-sm font-medium text-white">BaseLinker</span>
          <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
            {t("connectPanels.common.connected")}
          </Badge>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => disconnectMutation.mutate()}
          disabled={disconnectMutation.isPending}
          className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
        >
          {t("connectPanels.common.disconnect")}
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => insightsMutation.mutate()}
          disabled={insightsMutation.isPending}
          className="bg-[#0066CC] hover:bg-[#0052a3] text-white text-xs"
        >
          {insightsMutation.isPending ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin mr-1" /> {t("connectPanels.common.fetching")}
            </>
          ) : (
            t("connectPanels.common.fetch")
          )}
        </Button>
      </div>

      {insightsData && insightsData.summary && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <ShoppingBag className="h-3.5 w-3.5 text-blue-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.common.orders")}
                </span>
              </div>
              <div className="text-xl font-bold text-blue-400 mt-1">
                {insightsData.summary.totalOrders}
              </div>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.common.revenue")}
                </span>
              </div>
              <div className="text-xl font-bold text-emerald-400 mt-1">
                {(Number(insightsData.summary.totalRevenue) || 0).toFixed(0)}{" "}
                {insightsData.summary.currency ?? ""}
              </div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <Package className="h-3.5 w-3.5 text-amber-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.common.products")}
                </span>
              </div>
              <div className="text-xl font-bold text-amber-400 mt-1">
                {insightsData.summary.totalProducts}
              </div>
            </div>
          </div>

          {insightsData.summary.topProducts?.length > 0 && (
            <div>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white/70 transition-colors w-full"
              >
                {showDetails ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
                <span className="font-medium">
                  {t("connectPanels.baselinker.topProducts", {
                    count: Math.min(insightsData.summary.topProducts.length, 10),
                  })}
                </span>
              </button>
              {showDetails && (
                <div className="mt-2 rounded-lg border border-white/[0.08] overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[#0a0c10]/80 text-white/60">
                        <th className="text-left p-2 font-medium">
                          {t("connectPanels.common.product")}
                        </th>
                        <th className="text-right p-2 font-medium">
                          {t("connectPanels.common.sold")}
                        </th>
                        <th className="text-right p-2 font-medium">
                          {t("connectPanels.common.revenue")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {insightsData.summary.topProducts.slice(0, 10).map((p, i) => {
                        const revenue = Number(p.revenue) || 0;
                        const quantity = Number(p.quantity) || 0;
                        return (
                          <tr key={i} className="border-t border-white/[0.06]">
                            <td className="p-2 text-white max-w-[200px] truncate">
                              {p.name ?? "—"}
                            </td>
                            <td className="p-2 text-right text-blue-400">{quantity}</td>
                            <td className="p-2 text-right text-emerald-400">
                              {revenue.toFixed(0)} {insightsData.summary.currency ?? ""}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
              {t("connectPanels.common.dataLoaded")}
            </Badge>
            <span className="text-xs text-white/40">
              {t("connectPanels.baselinker.contextAttached")}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
